// Profile Meta Component
// SEO and social media meta tags for player profiles

import React, { useEffect } from 'react';

const ProfileMeta = ({ profile, isOwner }) => {
  // Generate meta description
  const getMetaDescription = () => {
    if (!profile) return '';
    const parts = [];
    
    if (profile.headline) {
      parts.push(profile.headline);
    } else {
      parts.push(`${profile.firstName} ${profile.lastName}'s sports profile`);
    }

    if (profile.athleticProfile?.primarySport) {
      parts.push(`${profile.athleticProfile.primarySport} player`);
    }

    if (profile.athleticProfile?.positions?.primary) {
      parts.push(`Position: ${profile.athleticProfile.positions.primary}`);
    }

    if (profile.location?.city && profile.location?.country) {
      parts.push(`${profile.location.city}, ${profile.location.country}`);
    }

    return parts.join(' • ');
  };

  // Generate keywords
  const getKeywords = () => {
    if (!profile) return 'sports profile, athlete, player profile';
    
    const keywords = [
      profile.firstName,
      profile.lastName,
      'sports profile',
      'athlete',
      'player profile'
    ];

    if (profile.athleticProfile?.primarySport) {
      keywords.push(
        profile.athleticProfile.primarySport,
        `${profile.athleticProfile.primarySport} player`
      );
    }

    if (profile.athleticProfile?.positions?.primary) {
      keywords.push(profile.athleticProfile.positions.primary);
    }

    if (profile.skills) {
      Object.values(profile.skills).forEach(category => {
        category.forEach(skill => {
          if (skill.level >= 7) { // Only high-level skills
            keywords.push(skill.name);
          }
        });
      });
    }

    return keywords.join(', ');
  };

  // Generate structured data for search engines
  const getStructuredData = () => {
    if (!profile) return JSON.stringify({});
    
    const data = {
      "@context": "https://schema.org",
      "@type": "Person",
      "name": `${profile.firstName} ${profile.lastName}`,
      "url": window.location.href,
      "sameAs": []
    };

    if (profile.profilePicture) {
      data.image = profile.profilePicture;
    }

    if (profile.bio) {
      data.description = profile.bio;
    }

    if (profile.socialLinks?.linkedin) {
      data.sameAs.push(profile.socialLinks.linkedin);
    }

    if (profile.socialLinks?.twitter) {
      data.sameAs.push(profile.socialLinks.twitter);
    }

    if (profile.location) {
      data.address = {
        "@type": "PostalAddress",
        "addressLocality": profile.location.city,
        "addressRegion": profile.location.state,
        "addressCountry": profile.location.country
      };
    }

    if (profile.athleticProfile?.primarySport) {
      data.athlete = {
        "@type": "Athlete",
        "sport": profile.athleticProfile.primarySport
      };
    }

    return JSON.stringify(data);
  };

  const title = profile 
    ? `${profile.firstName} ${profile.lastName} - Sports Profile | Sport Loop`
    : 'Sports Profile | Sport Loop';
  const description = getMetaDescription();
  const imageUrl = profile?.profilePicture || profile?.coverPhoto || '/default-profile-og.jpg';

  // Update document meta tags using vanilla JS
  useEffect(() => {
    if (!profile) return;
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (property, content, isProperty = false) => {
      const attrName = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attrName}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', getKeywords());

    // Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:image', imageUrl, true);
    updateMetaTag('og:url', window.location.href, true);
    updateMetaTag('og:type', 'profile', true);
    updateMetaTag('profile:first_name', profile.firstName, true);
    updateMetaTag('profile:last_name', profile.lastName, true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image');
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);
    updateMetaTag('twitter:image', imageUrl);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', window.location.href);

    // Robots meta
    if (profile.privacySettings?.searchable === false) {
      updateMetaTag('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if it exists
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) {
        robotsMeta.remove();
      }
    }

    // Structured data
    let structuredDataScript = document.querySelector('script[type="application/ld+json"]');
    if (!structuredDataScript) {
      structuredDataScript = document.createElement('script');
      structuredDataScript.setAttribute('type', 'application/ld+json');
      document.head.appendChild(structuredDataScript);
    }
    structuredDataScript.textContent = getStructuredData();

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'Sport Loop';
    };
  }, [profile, title, description, imageUrl]);

  return null;
};

export default ProfileMeta;
